import { useState } from "react";
import { PhoneInput } from "./PhoneInput";
import { Link } from "@/lib/router-compat";
import { cn } from "@/lib/utils";

interface ContactFormProps {
  config: any;
  formValues: Record<string, string>;
  setFormValues: (v: Record<string, string>) => void;
  submitForm: (e: React.FormEvent) => void;
  submitting: boolean;
  formError: string | null;
  detectedCountry?: any;
  invalidPhoneField?: string;
  compact?: boolean;
}

export function ContactForm({
  config,
  formValues,
  setFormValues,
  submitForm,
  submitting,
  formError,
  detectedCountry,
  invalidPhoneField,
  compact = false,
}: ContactFormProps) {
  const fields = config?.contactForm?.fields ?? [];
  const compliance = config?.compliance;
  // A2P 10DLC SMS consent — the box must NEVER be pre-checked. The user has to
  // click voluntarily. Consent text always names the business, states message
  // frequency, includes STOP/HELP instructions, and links to ToS + Privacy.
  const [consented, setConsented] = useState(false);
  const consentRequired = true;
  const showCheckbox = true;

  return (
    <form
      onSubmit={(e) => {
        if (consentRequired && !consented) {
          e.preventDefault();
          return;
        }
        submitForm(e);
      }}
      className={cn("space-y-3", compact ? "" : "flex flex-1 flex-col p-4")}
    >
      {fields.map((f: any) => (
        <div key={f.value}>
          <label className="mb-1 block text-xs font-semibold text-foreground">
            {f.label}
            {f.required && " *"}
          </label>
          {f.dataType === "TEXTAREA" ? (
            <textarea
              name={f.value}
              required={f.required}
              placeholder={f.placeholder}
              value={formValues[f.value] ?? ""}
              onChange={(e) =>
                setFormValues({ ...formValues, [f.value]: e.target.value })
              }
              rows={compact ? 2 : 3}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          ) : f.dataType === "PHONE" ? (
            <PhoneInput
              value={formValues[f.value] ?? ""}
              onChange={(v) => setFormValues({ ...formValues, [f.value]: v })}
              name={f.value}
              required={f.required}
              placeholder={f.placeholder}
              detectedCountry={detectedCountry}
              invalidMessage={
                invalidPhoneField === f.value
                  ? "Please enter a valid phone number."
                  : undefined
              }
            />
          ) : (
            <input
              type={f.dataType === "EMAIL" ? "email" : "text"}
              name={f.value}
              required={f.required}
              placeholder={f.placeholder}
              value={formValues[f.value] ?? ""}
              onChange={(e) =>
                setFormValues({ ...formValues, [f.value]: e.target.value })
              }
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          )}
        </div>
      ))}

      {showCheckbox && (
        <label className="flex items-start gap-2 text-[11px] leading-relaxed text-muted-foreground">
          <input
            type="checkbox"
            checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
            className="mt-0.5 shrink-0"
          />
          <span>
            By checking this box and submitting, you agree to receive SMS or
            email messages from Scotty&apos;s Land Specialist at the number
            provided. Message &amp; data rates may apply. Message frequency
            varies. Reply STOP to cancel at any time, or HELP for help. View our{" "}
            <Link
              to="/terms-and-conditions"
              className="font-semibold text-[#D96B27] underline"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy-policy"
              className="font-semibold text-[#D96B27] underline"
            >
              Privacy Policy
            </Link>
            .
          </span>
        </label>
      )}

      {formError && <p className="text-xs text-destructive">{formError}</p>}

      <button
        type="submit"
        disabled={submitting || (consentRequired && !consented)}
        className="w-full rounded-full bg-[#D96B27] px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#BA541B] disabled:opacity-50"
      >
        {submitting ? "Sending…" : "Send"}
      </button>
    </form>
  );
}
